import React from "react";
import { Flex, Typography } from "antd";
import { Handle, Position } from "reactflow";
import { actionCodeToTitle, getActionIconAndClass } from "../utils/actionCodeMapping";
import { useWorkflowStore } from "../store";

export interface ActionNodeData {
    /** API action code (e.g. Action.Message.AutomatedResponse) or legacy short type */
    actionType?: string;
    /** API action code when using workflow enums */
    actionCode?: string;
    title?: string;
    isPlaceholder?: boolean;
    // AutomatedResponse
    message?: string;
    onceOnly?: boolean;
    responseType?: string;
    /** Template ID when a template is selected (for preview restore) */
    templateId?: string;
    /** Template display name (friendlyName) for node display */
    templateName?: string;
    /** Integration ID for template (needed for preview fetch on restore) */
    templateIntegId?: string;
    /** True when media files are attached (no template) */
    hasMedia?: boolean;
    /** Media files for display/restore when switching nodes */
    mediaFileList?: unknown[];
    /** Template components from preview (for WhatsApp templates) */
    components?: unknown;
    /** Template variable values (field name → value) */
    fieldNameAndValueMap?: Record<string, unknown>;
    // NotifyUser
    notificationTypes?: string[];
    /** @deprecated Use recordOwner/accountOwner instead */
    notifyUser?: string;
    notifyUserLabel?: string;
    notifyMessage?: string;
    /** Include record owner in email notification */
    recordOwner?: boolean;
    /** Include account owner in email notification */
    accountOwner?: boolean;
    /** Include custom email addresses */
    includeAdditionalEmails?: boolean;
    /** Email addresses when "Additional Email Addresses" is selected */
    additionalEmails?: string[];
    // AddNote / LogActivity (keys aligned with settingConfig.js loggingPreference)
    /** Merge all activities into a single activity as notes? (true/false) */
    logRecordAsThread?: string;
    /** Where to log: "all" | "recent" */
    logRecordsFor?: string;
}

const ACTION_META: Record<string, { defaultTitle: string }> = {
    AutomatedResponse: { defaultTitle: "Automated Response" },
    NotifyUser: { defaultTitle: "Notify User" },
    CreateLead: { defaultTitle: "Create Lead" },
    LogActivity: { defaultTitle: "Log Activity" },
    AddNote: { defaultTitle: "Add Note" }
};

const DEFAULT_EMAIL_NOTIFICATION_MESSAGE =
    "You have received a new notification. Please check your account for details.";

const ACTION_CODE_TO_META_KEY: Record<string, string> = {
    "Action.Message.AutomatedResponse": "AutomatedResponse",
    "Action.Notify.NotifyUser": "NotifyUser",
    "Action.Crm.AddNote": "AddNote",
    "Action.Crm.LogActivity": "LogActivity",
    "Action.Crm.CreateLead": "CreateLead",
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="trigger-rule-node__detail-row">
        <span className="trigger-rule-node__detail-label">{label}</span>
        <Flex gap="small" align="center" className="hs-fs-12 hs-fw-400 hs-color-dark-grey flex-warp">{children}</Flex>
    </div>
);

const Badge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className="trigger-rule-node__detail-badge">{children}</span>
);

const DEFAULT_NODE_ICON = { icon: <div className="automationIconsSprite select-icon" /> };

function getNodeIcon(data: ActionNodeData) {
    if (!data.actionCode && !data.actionType) return DEFAULT_NODE_ICON;
    return getActionIconAndClass(data.actionCode ?? data.actionType);
}

function getActionFormType(data: ActionNodeData): string {
    if (data.actionCode && ACTION_CODE_TO_META_KEY[data.actionCode]) {
        return ACTION_CODE_TO_META_KEY[data.actionCode];
    }
    return data.actionType ?? "";
}

const renderActionDetails = (data: ActionNodeData, applyOncePerContact?: boolean | null) => {
    const formType = getActionFormType(data);
    switch (formType) {
        case "AutomatedResponse":
            return (
                <>
                    {data.templateName && (
                        <DetailRow label="Template:">
                            <Badge>{data.templateName}</Badge>
                        </DetailRow>
                    )}
                    {data.responseType && <DetailRow label="Response:"><Badge>{data.responseType}</Badge></DetailRow>}
                    {!data.templateName && data.message && (
                        <DetailRow label="Message:">
                            <span className="trigger-rule-node__detail-value--message">&ldquo;{data.message}&rdquo;</span>
                        </DetailRow>
                    )}
                    {!data.templateName && !data.message && data.hasMedia && (
                        <DetailRow label="Message:">
                            <Badge>Media files attached</Badge>
                        </DetailRow>
                    )}
                    {(applyOncePerContact ?? data.onceOnly) && (
                        <DetailRow label="Apply once per contact:">
                            <Badge>Yes</Badge>
                        </DetailRow>
                    )}
                </>
            );
        case "NotifyUser":
            const recipients: string[] = [];
            if (data.recordOwner) recipients.push("Record Owner");
            if (data.accountOwner) recipients.push("Account Owner");
            if (data.includeAdditionalEmails && data.additionalEmails?.length) {
                recipients.push(...data.additionalEmails);
            }
            if (recipients.length === 0 && data.notifyUserLabel) {
                recipients.push(data.notifyUserLabel);
            }
            return (
                <>
                    {recipients.length > 0 && (
                        <DetailRow label="Send to:">
                            <Flex gap="small" align="center" className="trigger-rule-node__detail-value--wrap hs-fs-12 hs-fw-400 hs-color-dark-grey flex-warp">
                                {recipients.map((r) => (
                                    <Badge key={r}>
                                        <Typography.Text ellipsis={{ tooltip: r }} className="trigger-rule-node__detail-email">
                                            {r}
                                        </Typography.Text>
                                    </Badge>
                                ))}
                            </Flex>
                        </DetailRow>
                    )}
                    <DetailRow label="Message:">
                        <span className="trigger-rule-node__detail-value--message">
                            &ldquo;{data.notifyMessage ?? DEFAULT_EMAIL_NOTIFICATION_MESSAGE}&rdquo;
                        </span>
                    </DetailRow>
                </>
            );
        case "AddNote":
            return (
                <>
                    {data.logRecordAsThread && (
                        <DetailRow label="Merge:">
                            <Badge>{data.logRecordAsThread === "true" ? "Yes" : "No"}</Badge>
                        </DetailRow>
                    )}
                    {data.logRecordsFor && (
                        <DetailRow label="Log to:">
                            <Badge>{data.logRecordsFor === "recent" ? "Recent record" : "All matching records"}</Badge>
                        </DetailRow>
                    )}
                </>
            );
        case "LogActivity":
            return (
                <>
                    {data.logRecordAsThread && (
                        <DetailRow label="Merge:">
                            <Badge>{data.logRecordAsThread === "true" ? "Yes" : "No"}</Badge>
                        </DetailRow>
                    )}
                    {data.logRecordsFor && (
                        <DetailRow label="Log to:">
                            <Badge>{data.logRecordsFor === "recent" ? "Recent record" : "All matching records"}</Badge>
                        </DetailRow>
                    )}
                </>
            );
        default:
            return null;
    }
};

function getActionMeta(data: ActionNodeData) {
    const key = data.actionCode
        ? ACTION_CODE_TO_META_KEY[data.actionCode] ?? data.actionType
        : data.actionType;
    return key ? ACTION_META[key] : null;
}

const ActionNodeComponent: React.FC<{ data: ActionNodeData; selected?: boolean }> = ({ data, selected }) => {
    const hasTypeSelected = !!(data.actionCode || data.actionType);
    const isPlaceholder = data.isPlaceholder ?? !hasTypeSelected;
    const meta = getActionMeta(data);
    const applyOncePerContact = useWorkflowStore((s) => s.applyOncePerContact);

    const displayTitle = isPlaceholder
        ? "Select an Action..."
        : (data.title ?? meta?.defaultTitle ?? actionCodeToTitle(data.actionCode) ?? "Action");

    return (
        <div
            className={`trigger-rule-node hs-border-grey trigger-rule-node--action ${isPlaceholder ? "trigger-rule-node--placeholder" : ""} ${selected ? "trigger-rule-node--selected" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={`Action: ${displayTitle}. ${selected ? "Selected" : "Click to select"}`}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).click();
                }
            }}
        >
            <Handle type="target" position={Position.Top} id="target" className="trigger-rule-node__handle" />
            <Handle type="source" position={Position.Bottom} id="source" className="trigger-rule-node__handle" />
            <span className={`trigger-rule-node__tag ${isPlaceholder ? "tag__fill" : ""}`}>{isPlaceholder ? "New Step" : "Action"}</span>

            <div className="trigger-rule-node__header-row">
                <div className="trigger-rule-node__icon">
                    {getNodeIcon(data).icon}
                </div>
                <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">
                    {isPlaceholder
                        ? "Select an Action..."
                        : (data.title ?? meta?.defaultTitle ?? actionCodeToTitle(data.actionCode) ?? "Action")}
                </span>
            </div>

            {isPlaceholder ? (
                <span className="trigger-rule-node__desc">Choose from the panel on the right</span>
            ) : (
                <Flex vertical gap="small" justify="space-between" className=" py-3">{renderActionDetails(data, applyOncePerContact)}</Flex>
            )}
        </div>
    );
};

export const ActionNode = React.memo(ActionNodeComponent);
